import { ensureSupabaseAccessTokenFresh, getAuthenticatedSupabase } from '../../supabaseClient';

export const INVITATION_PUBLIC_BUCKET = 'invitation-public';

const MAX_BYTES = 5 * 1024 * 1024;

/** Srozumitelná hláška, když v projektu chybí bucket (migrace nebyla spuštěna). */
export function userFacingInvitationStorageError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('bucket') && (lower.includes('not found') || lower.includes('does not exist'))) {
    return (
      'Úložiště pro fotky pozvánky není v Supabase vytvořené. V Dashboardu → SQL → spusťte skript ' +
      '`supabase/migration_invitation_public_images.sql` (vytvoří veřejný bucket `invitation-public` a oprávnění).'
    );
  }
  if (lower.includes('exp') && lower.includes('claim') && lower.includes('timestamp')) {
    return (
      'Platnost přihlášení pro nahrávání vypršela. Obnovte stránku (F5) nebo se odhlaste a znovu přihlaste, pak zkuste nahrát znovu.'
    );
  }
  return message;
}

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

export type InvitationPhotoSlot =
  | 'bride'
  | 'groom'
  | 'hero'
  | 'cateringMain'
  | 'cateringWarm'
  | 'cateringCold'
  | 'cateringSweet'
  | 'cateringDrinks';

function extensionForFile(file: File): string {
  if (file.type === 'image/jpeg') return 'jpg';
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  if (file.type === 'image/gif') return 'gif';
  const m = file.name.match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : 'jpg';
}

function randomId(): string {
  return crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Nahraje obrázek do veřejného bucketu a vrátí public URL pro uložení do public_config.
 */
export async function uploadInvitationPublicImage(
  weddingId: string,
  slot: InvitationPhotoSlot,
  file: File,
): Promise<string> {
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error('Povolené jsou obrázky JPEG, PNG, WebP nebo GIF.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Soubor je větší než 5 MB.');
  }

  const ext = extensionForFile(file);
  const path = `${weddingId}/invitation/${slot}-${randomId()}.${ext}`;
  await ensureSupabaseAccessTokenFresh();
  const supabase = getAuthenticatedSupabase();

  const { error } = await supabase.storage.from(INVITATION_PUBLIC_BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: false,
  });

  if (error) {
    throw new Error(userFacingInvitationStorageError(error.message));
  }

  const { data } = supabase.storage.from(INVITATION_PUBLIC_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

/** Vrátí storage path z public URL bucketu invitation-public, nebo null (cizí URL). */
export function extractInvitationStoragePathFromPublicUrl(publicUrl: string): string | null {
  const marker = `/object/public/${INVITATION_PUBLIC_BUCKET}/`;
  const i = publicUrl.indexOf(marker);
  if (i === -1) return null;
  const rest = publicUrl.slice(i + marker.length);
  return rest.split('?')[0] ?? null;
}

export async function removeInvitationPublicImageByUrl(publicUrl: string): Promise<void> {
  const path = extractInvitationStoragePathFromPublicUrl(publicUrl);
  if (!path) return;
  await ensureSupabaseAccessTokenFresh();
  const supabase = getAuthenticatedSupabase();
  const { error } = await supabase.storage.from(INVITATION_PUBLIC_BUCKET).remove([path]);
  if (error) throw new Error(userFacingInvitationStorageError(error.message));
}
